import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

// Helper function to compress images
const loadImage = (url) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            // Resize image to reduce PDF size
            const maxSize = 100;
            let width = img.width;
            let height = img.height;
            
            if (width > height) {
                if (width > maxSize) {
                    height = height * (maxSize / width);
                    width = maxSize;
                }
            } else {
                if (height > maxSize) {
                    width = width * (maxSize / height);
                    height = maxSize;
                }
            }
            
            canvas.width = width;
            canvas.height = height;
            
            ctx.drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/jpeg', 0.7));
        };
        img.onerror = reject;
        img.src = url;
    });
};

export const exportToPDF = async (filteredInventory, selectedVariants, coreProducts, usePO2 = false) => {
    const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
    });

    // Filter products that have values in the selected PO type
    const productsToExport = filteredInventory.filter(product => 
        product.variants.some(variant => {
            const key = usePO2 ? `po2-stock-input-${variant.id}` : `po-stock-input-${variant.id}`;
            const savedValue = localStorage.getItem(key);
            return savedValue && parseInt(savedValue) > 0;
        })
    );

    // Define dimensions
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const imageSize = 12;
    const spacing = 2;
    const itemHeight = 15;
    const variantBoxWidth = 25; // Reduced width
    const variantBoxHeight = 8;
    const maxVariantsPerRow = Math.floor((pageWidth - margin - imageSize - spacing - 70) / (variantBoxWidth + spacing));

    // Add header
    doc.setFontSize(14);
    doc.text('Purchase Order', margin, margin);
    doc.setFontSize(8);
    doc.text(`Date: ${new Date().toLocaleDateString()}`, pageWidth - margin - 20, margin);
    doc.text(`Type: ${usePO2 ? 'PO New2' : 'PO New'}`, pageWidth - margin - 20, margin + 6);
    let currentY = margin + 15;

    // Set default styles
    doc.setFontSize(8);
    doc.setLineWidth(0.1);

    let totalProducts = 0;
    let totalNewItems = 0;

    for (const product of productsToExport) {
        // Check if we need a new page
        if (currentY + itemHeight > pageHeight - margin) {
            doc.addPage();
            currentY = margin + 15;
        }

        // Add product image
        if (product.image_url) {
            try {
                const compressedImage = await loadImage(product.image_url);
                doc.addImage(compressedImage, 'JPEG', margin, currentY + 1, imageSize, imageSize);
            } catch (error) {
                console.error('Error loading image:', error);
            }
        }

        // Product title and SKU
        const titleX = margin + imageSize + spacing;
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(product.product_title.substring(0, 40), titleX, currentY + 4);
        doc.setFontSize(7);
        doc.setTextColor(0, 0, 0);
        doc.text(`SKU: ${coreProducts[product.product_id]?.sku || product.variants[0]?.sku || 'N/A'}`, titleX, currentY + 8);

        // Variant boxes
        let variantX = titleX + 70;
        let variantCount = 0;
        
        // Filter variants that have values in the selected PO type
        const variantsWithStock = product.variants
            .filter(variant => {
                const key = usePO2 ? `po2-stock-input-${variant.id}` : `po-stock-input-${variant.id}`;
                const newStock = localStorage.getItem(key);
                return newStock && parseInt(newStock) > 0;
            });

        variantsWithStock.forEach(variant => {
            // Check if we need to move to next row
            if (variantCount >= maxVariantsPerRow) {
                currentY += itemHeight;
                variantX = titleX + 70;
                variantCount = 0;
            }

            // Get value from the selected PO type
            const key = usePO2 ? `po2-stock-input-${variant.id}` : `po-stock-input-${variant.id}`;
            const newStock = parseInt(localStorage.getItem(key)) || 0;
            totalNewItems += newStock;
            totalProducts++;
            
            // Draw variant box
            doc.setFillColor(248, 249, 250);
            doc.rect(variantX, currentY, variantBoxWidth, variantBoxHeight, 'F');
            
            // Add variant text and stock number side by side
            doc.setFontSize(6);
            doc.setTextColor(0, 0, 0);
            doc.text(variant.title, variantX + 2, currentY + 4);
            doc.setFontSize(8);
            doc.setTextColor(220, 53, 69);
            doc.text(`+${newStock}`, variantX + variantBoxWidth - 4, currentY + 4, { align: 'right' });

            variantX += variantBoxWidth + spacing;
            variantCount++;
        });

        currentY += itemHeight;
    }

    // Add totals at the bottom
    currentY += 10;
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(`Total Products: ${totalProducts} variants`, margin, currentY);
    doc.text(`Total New Items: ${totalNewItems} pieces`, margin, currentY + 6);

    doc.save('purchase_order.pdf');
}; 